import { Injectable, OnDestroy } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  EMPTY,
  of,
  Subscription,
  throwError,
  from,
} from 'rxjs';
import { map, catchError, switchMap, finalize, tap } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CompanyService } from 'src/app/services/companies.service';
import { OfficeService } from 'src/app/services/offices.service';
import { PermissionsService } from 'src/app/core/menu/permissions.service';
import { PartnerSignupService } from 'src/app/services/partner-signup.service';
import { PartnerInvitationService } from 'src/app/services/partner-invitation.service';
import { UserPermissions } from 'src/app/models/user-permissions.interface';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';

export type UserType = UserModel | undefined;

export interface LoginResponse {
  token: string;
  user: any;
  requiresTwoFactor?: boolean;
  temp_token?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private unsubscribe: Subscription[] = [];
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  private API_LOGIN = `${environment.laravelApi}${environment.login}`;
  private API_FORGOT_PASSWORD = `${environment.laravelApi}${environment.forgotPassword}`;
  private API_RESET_PASSWORD = `${environment.laravelApi}${environment.resetPassword}`;
  private API_LOGIN_2FA = `${environment.laravelApi}${environment.login}${environment.verifiyCode}`;
  private API_ME = `${environment.laravelBackoffice}/me`;
  private API_SOCIAL_REGISTER = `${environment.laravelBackoffice}/social-register`;
  private API_OTP_SMS_SEND = `${environment.laravelApi}/sms/send/login`;
  private API_OTP_SMS_VERIFY = `${environment.laravelApi}/sms/verify/login`;
  private API_OTP_EMAIL_SEND = `${environment.laravelApi}/email/send/login`;
  private API_OTP_EMAIL_VERIFY = `${environment.laravelApi}/email/verify/login`;

  private currentTokenSubject!: string;
  private deviceId: string;

  private currentUserSubject = new BehaviorSubject<LoginResponse | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private meSubject = new BehaviorSubject<any>(null);
  me$ = this.meSubject.asObservable();

  private roleSubject = new BehaviorSubject<string>('');
  currentRole$ = this.roleSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  /**
   * Cómo se entró en esta sesión de la app: `google` tras «Continuar con
   * Google». El paso 2 del registro lo usa para el chip de la cuenta y para
   * proponer la empresa a partir del dominio. Solo en memoria.
   */
  lastSignInMethod: 'google' | null = null;

  get currentUserValue(): LoginResponse | null {
    return this.currentUserSubject.value;
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private http: HttpClient,
    private router: Router,
    private companyService: CompanyService,
    private permissionsService: PermissionsService,
    private partnerSignup: PartnerSignupService,
    private partnerInvitation: PartnerInvitationService,
  ) {
    this.deviceId = this.getOrCreateDeviceId();
    this.restoreUserFromStorage();
  }

  private getOrCreateDeviceId(): string {
    const DEVICE_ID_KEY = 'device_id';
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId = this.generateUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  public restoreUserFromStorage(): void {
    const userJson = localStorage.getItem('currentUser');
    const corporation = localStorage.getItem('selectedCompanyId') || '';

    if (corporation) {
      this.companyService.setCompanyId(corporation);
    }
    const user: LoginResponse | null = userJson ? JSON.parse(userJson) : null;

    // `user.user` puede faltar si quedó guardada una sesión de una versión
    // anterior con un formato distinto (p.ej. un bug ya corregido en el alta
    // manual, que guardaba la respuesta cruda del backend sin mapear a
    // `LoginResponse`) — se trata como sesión inválida en vez de petar el
    // arranque de toda la app.
    if (user && user.user && this.isTokenValid(user.token)) {
      this.currentUserSubject.next(user);
      // Fuente única de verdad del rol: el usuario autenticado (backend). No se
      // usa un `selectedRole` de localStorage, que podía divergir del rol real.
      if (user.user.role) {
        this.roleSubject.next(user.user.role);
      }
      // Refrescar datos del perfil
      this.getMe().subscribe({
        next: () => { },
        error: (err) => {
          console.error(
            '[AuthService] Failed to load permissions after restore:',
            err,
          );
        },
      });
    } else {
      this.clearStorage();
    }
  }

  login(email: string, password: string): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);
    this.lastSignInMethod = null;

    return this.http
      .post<LoginResponse>(this.API_LOGIN, {
        email,
        password,
        device_id: this.deviceId,
        name: 'dashboard',
        platform: 'web',
      })
      .pipe(
        tap((result) => {
          if (!result.token && result.temp_token) {
            this.currentTokenSubject = result.temp_token;
          }
        }),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  /**
   * Códigos de Firebase que NO son un fallo del alta, sino que la ventana de
   * Google no llegó a completarse:
   *
   * - `popup-closed-by-user`: el usuario la cerró a propósito.
   * - `cancelled-popup-request`: una segunda llamada ha reemplazado a esta
   *   (p.ej. doble clic en el botón). Es el caso que engañaba: la PRIMERA
   *   petición muere con este error mientras la segunda sigue viva y puede
   *   registrar al usuario perfectamente — de ahí el «sale error pero a veces
   *   sí te registra».
   * - `user-cancelled`: el usuario denegó los permisos.
   *
   * En los tres casos no hay nada que contarle al usuario: simplemente no ha
   * pasado nada.
   */
  private static readonly GOOGLE_CANCELLED_CODES = [
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled',
  ];

  signInWithGoogle(): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const auth = getAuth();

    return from(signInWithPopup(auth, provider)).pipe(
      // El silencio es SOLO para las cancelaciones del popup. Antes este
      // `catchError` envolvía también a `socialRegister`, así que cualquier
      // fallo del backend que trajera un `code` coincidente se tragaba sin
      // avisar: la pantalla se quedaba igual y el usuario no sabía nada.
      catchError((err) => {
        if (AuthService.GOOGLE_CANCELLED_CODES.includes(err?.code)) return EMPTY;
        return throwError(() => err);
      }),
      switchMap((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const accessToken = credential?.accessToken;
        if (!accessToken) {
          return throwError(() => new Error('No se pudo obtener el access_token de Google.'));
        }

        const nameParts = (result.user.displayName ?? '').trim().split(/\s+/);
        const name = nameParts[0] ?? '';
        const surname = nameParts.slice(1).join(' ');

        // social-register ya resuelve el caso de usuario existente (busca
        // por email antes de crear uno nuevo), así que basta con este único
        // endpoint tanto para login como para alta con Google.
        return this.socialRegister(accessToken, name, surname);
      }),
      tap((res) => (this.lastSignInMethod = res?.token ? 'google' : this.lastSignInMethod)),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  /**
   * Con una invitación al equipo de un partner en curso va su
   * `invitation_token`: el email de Google debe ser el de la invitación (si no,
   * 422 y no hay sesión). Cuenta nueva: recibe el rol invitado; existente:
   * acepta la invitación y entra. Bien en ambos casos, ya no queda pendiente.
   */
  private socialRegister(
    accessToken: string,
    name: string,
    surname: string,
  ): Observable<LoginResponse> {
    const invitationToken = this.partnerInvitation.pending?.token;
    return this.http
      .post<LoginResponse>(this.API_SOCIAL_REGISTER, {
        provider_name: 'google',
        access_token: accessToken,
        name,
        surname,
        ...(invitationToken ? { invitation_token: invitationToken } : {}),
      })
      .pipe(tap((res) => invitationToken && res?.token && this.partnerInvitation.clear()));
  }

  verifyCodeSms(code: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-2FA-Token': this.currentTokenSubject,
    });

    this.isLoadingSubject.next(true);

    return this.http
      .post<LoginResponse>(this.API_LOGIN_2FA, { code }, { headers })
      .pipe(
        tap((user) => {
          this.setUser(user);
        }),
        catchError((err) => {
          console.error('OTP error', err);
          return throwError(() => err);
        }),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  /**
   * Passwordless login: send a 4-digit code by SMS to a phone that is
   * already registered in the backend.
   */
  sendOtpSms(phone: string): Observable<void> {
    this.isLoadingSubject.next(true);

    return this.http.post<void>(this.API_OTP_SMS_SEND, { phone }).pipe(
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  verifyOtpSms(phone: string, code: string): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<LoginResponse>(this.API_OTP_SMS_VERIFY, { phone, code }).pipe(
      tap((user) => this.setUser(user)),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  /**
   * Passwordless login: send a 4-digit code by email to an address that is
   * already registered in the backend.
   */
  sendOtpEmail(email: string): Observable<void> {
    this.isLoadingSubject.next(true);

    return this.http.post<void>(this.API_OTP_EMAIL_SEND, { email }).pipe(
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  verifyOtpEmail(email: string, code: string): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);

    return this.http.post<LoginResponse>(this.API_OTP_EMAIL_VERIFY, { email, code }).pipe(
      tap((user) => this.setUser(user)),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  setUser(user: LoginResponse): void {
    // Sin token no hay sesión: persistirlo guardaría la cadena "undefined" en
    // `authToken` y la siguiente llamada saldría sin cabecera `Authorization`
    // (el backend responde `AUTH_CREDENTIAL_MISSING`).
    if (!user?.token) {
      console.error('[AuthService] setUser() sin token: no se inicia sesión', user);
      return;
    }
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('authToken', user.token);
    // Todo inicio de sesión o registro pasa por aquí: la marca de partner de
    // la pantalla de acceso no sigue a la sesión ni se guarda (R5).
    this.partnerSignup.commitOnAuth();
    this.getMe().subscribe({
      next: () => { },
      error: (err) => {
        console.error(
          '[AuthService] Failed to load permissions after login:',
          err,
        );
      },
    });

    // Fuente única de verdad del rol: el usuario autenticado. No se persiste un
    // `selectedRole` independiente y modificable. `user.user` puede faltar
    // (p.ej. un alta recién hecha, sin corporación ni rol todavía) — no debe
    // romper el flujo de login/registro.
    if (user.user?.role) {
      this.roleSubject.next(user.user.role);
    }

    this.currentUserSubject.next(user);

    if (user.user?.role === 'admin') {
      this.companyService.getAllCompanies$().subscribe();
    } else {
      if (user.user?.corporation_user?.corporation_office?.corporation_id) {
        this.companyService.setCompanyId(
          user.user.corporation_user.corporation_office.corporation_id,
        );
      }
    }
  }

  /**
   * La respuesta del login o del alta no siempre trae nombre y email (p. ej. un
   * alta recién hecha, sin empresa ni rol todavía): sin ellos el menú de sesión
   * sale con «?». Se completan con los de `/me` y se guardan con la sesión.
   */
  private completeSessionUser(me: any): void {
    const current = this.currentUserSubject.value;
    if (!current || !me) return;
    const user = current.user ?? {};
    const name = user.name || me.name;
    const email = user.email || me.email;
    if (name === user.name && email === user.email) return;
    const next: LoginResponse = { ...current, user: { ...user, name, email } };
    localStorage.setItem('currentUser', JSON.stringify(next));
    this.currentUserSubject.next(next);
  }

  getMe(): Observable<any> {
    // Sin credencial la petición solo puede fallar con `AUTH_CREDENTIAL_MISSING`:
    // se corta aquí para que el error sea explícito y no parezca del backend.
    if (!localStorage.getItem('authToken')) {
      const err = new Error('[AuthService] getMe() sin token: no hay sesión iniciada');
      console.error(err.message);
      this.permissionsService.loadPermissionsError$.next(err);
      return throwError(() => err);
    }
    return this.http.get<any>(this.API_ME).pipe(
      tap((me) => {
        this.meSubject.next(me);
        this.permissionsService.setPermissions(me as UserPermissions);
        this.completeSessionUser(me);
      }),
      catchError((err) => {
        console.error('[AuthService] Error calling /backoffice/me:', err);
        this.permissionsService.loadPermissionsError$.next(err);
        return throwError(() => err);
      }),
    );
  }

  retryLoadPermissions(): void {
    this.getMe().subscribe({
      next: () => { },
      error: (err) => {
        console.error('[AuthService] Failed to load permissions:', err);
      },
    });
  }

  logout(redirectTo = '/auth/welcome'): void {
    this.clearStorage();
    this.router.navigate([redirectTo]);
  }

  private clearStorage(): void {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedRole');

    this.lastSignInMethod = null;
    this.currentUserSubject.next(null);
    this.roleSubject.next('');
    this.permissionsService.clearPermissions();
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp;
      const now = Math.floor(Date.now() / 1000);
      return expiry && now < expiry;
    } catch {
      return false;
    }
  }

  // El rol NO es modificable desde el cliente: su única fuente de verdad es el
  // usuario autenticado (backend). Por eso ya no existe `setRole()`; `getRole()`
  // y derivados son solo de lectura sobre ese valor autoritativo.
  getRole(): string {
    return this.roleSubject.value;
  }

  hasRole(role: string): boolean {
    return this.getRole() === role;
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.includes(this.getRole());
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);

    return this.http
      .post<any>(this.API_FORGOT_PASSWORD, {
        email,
        origin: 'dashboard',
        backOffice: true,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  resetPassword(
    token: string,
    email: string,
    password: string,
    password_confirmation: string,
  ): Observable<boolean> {
    this.isLoadingSubject.next(true);

    return this.http
      .post<any>(this.API_RESET_PASSWORD, {
        token,
        email,
        password,
        password_confirmation,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  /** Paso 1 del registro: sin teléfono, que se pide en el paso 2 (#20261023-090006). */
  registration(payload: {
    name: string;
    surname: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Observable<any> {
    this.isLoadingSubject.next(true);
    this.lastSignInMethod = null;
    // Invitación al equipo de un partner: el alta recibe el rol invitado en
    // vez de `create_corporation` y la invitación queda aceptada.
    const invitationToken = this.partnerInvitation.pending?.token;
    return this.authHttpService
      .registerUser(invitationToken ? { ...payload, invitation_token: invitationToken } : payload)
      .pipe(
        tap(() => invitationToken && this.partnerInvitation.clear()),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((s) => s.unsubscribe());
  }
}
